import React, { useEffect } from 'react';
import ServiceAccountSelector from './ServiceAccountSelector';
import CredentialsForm from './CredentialsForm';
import ArtWorkoutOtpForm from './ArtWorkoutOtpForm';
import CourseSelector from './CourseSelector';
import CourseGroupSelector from './CourseGroupSelector';
import SubscriptionSelector from './SubscriptionSelector';
import { useActivityConfiguration } from '../hooks/useActivityConfiguration';

/**
 * ActivityConfigurationFlow - Shared component for configuring an activity
 * Used by both ActivityCreator (non-wizard) and WizardActivityConfigStep (wizard mode)
 *
 * @param {Object} props
 * @param {number} childId - The child's ID
 * @param {string} childUsername - The child's name/username
 * @param {Object} template - The activity template to configure
 * @param {string} csrfToken - CSRF token for API requests
 * @param {Function} onComplete - Called when activity is created successfully
 * @param {Function} onBack - Called when user navigates back (to template selection)
 * @param {boolean} isWizardMode - Whether this is in wizard mode
 * @param {boolean} isTestMode - Whether to show test IDs
 * @param {Function} onSkip - (Wizard mode) Called when user skips this activity
 * @param {number} currentIndex - (Wizard mode) Current activity index
 * @param {number} totalCount - (Wizard mode) Total activities to configure
 */
const ActivityConfigurationFlow = ({
  childId,
  childUsername,
  template,
  csrfToken,
  onComplete,
  onBack,
  isWizardMode = false,
  isTestMode = false,
  onSkip,
  currentIndex,
  totalCount
}) => {
  const config = useActivityConfiguration({
    childId,
    csrfToken,
    template,
    onComplete,
    onSkip,
    isWizardMode
  });

  // Initialize flow when template is available
  useEffect(() => {
    if (template) {
      config.initializeFlow();
    }
  }, [template?.key]);

  // Loading state
  if (config.currentStep === 'loading') {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">
          {config.isSubmitting ? 'Đang thêm hoạt động...' : 'Đang tải...'}
        </p>
      </div>
    );
  }

  // Error state for preloaded activities
  if (config.currentStep === 'error') {
    return (
      <div>
        <div className="alert alert-danger mb-4">
          <strong>Lỗi:</strong> {config.submitError}
        </div>
        <div className="d-flex justify-content-between">
          <button type="button" className="btn btn-link text-muted" onClick={config.handleSkip}>
            Bỏ Qua Hoạt Động Này
          </button>
          <button type="button" className="btn btn-primary" onClick={config.handleCreatePreloadedActivity}>
            Thử Lại
          </button>
        </div>
      </div>
    );
  }

  // Render header based on mode
  // In wizard mode, always show "Cấu Hình {template.name}" as title for consistency
  const renderHeader = (title, description) => {
    if (isWizardMode) {
      return (
        <>
          {/* Progress indicator */}
          <div className="mb-3">
            <span className="badge bg-primary">
              Hoạt động {currentIndex + 1} / {totalCount}
            </span>
          </div>

          {/* Activity header - always "Cấu Hình" in wizard mode */}
          <div className="d-flex align-items-start mb-4">
            <div className="flex-shrink-0 me-3">
              <div className="rounded-2 overflow-hidden" style={{ width: '64px', height: '64px' }}>
                <img
                  src={template.icon_url}
                  alt={template.name}
                  className="w-100 h-100 object-fit-cover"
                  onError={(e) => {
                    e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect width='64' height='64' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='24' fill='%236c757d'%3E${template.name[0]}%3C/text%3E%3C/svg%3E`;
                  }}
                />
              </div>
            </div>
            <div>
              <h4 className="mb-1" data-testid="wizard-step-title">Cấu Hình {template.name}</h4>
              <p className="text-muted mb-0">{description || template.description}</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <div className="d-flex align-items-start mb-4">
        <div className="flex-shrink-0 me-3">
          <div className="rounded-2 overflow-hidden" style={{ width: '64px', height: '64px' }}>
            <img
              src={template.icon_url}
              alt={template.name}
              className="w-100 h-100 object-fit-cover"
              onError={(e) => {
                e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect width='64' height='64' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='24' fill='%236c757d'%3E${template.name[0]}%3C/text%3E%3C/svg%3E`;
              }}
            />
          </div>
        </div>
        <div>
          <h2 className="h4 fw-bold mb-1">{title}</h2>
          <p className="text-muted mb-0">{description}</p>
        </div>
      </div>
    );
  };

  // Wrapper for card in non-wizard mode
  const ContentWrapper = ({ children }) => {
    if (isWizardMode) {
      return <div>{children}</div>;
    }
    return (
      <div className="card border rounded-3">
        <div className="card-body p-4">
          {children}
        </div>
      </div>
    );
  };

  // Get back button text based on step
  const getBackButtonText = () => {
    if (config.currentStep === 'subscription') {
      return config.useCustomAccount ? 'Quay Lại Tên Tài Khoản' : 'Quay Lại Chọn Tài Khoản';
    }
    if (config.currentStep === 'courses') {
      if (config.showsSubscriptionCheckbox) return 'Quay Lại Loại Tài Khoản';
      return config.useCustomAccount ? 'Quay Lại Tên Tài Khoản' : 'Quay Lại Chọn Tài Khoản';
    }
    return 'Quay Lại';
  };

  // Get submit button text
  const getSubmitButtonText = () => {
    if (isWizardMode) return 'Xác Nhận';
    return 'Thêm Hoạt Động';
  };

  return (
    <div>
      {/* Service Account Selection */}
      {config.currentStep === 'service_account' && (
        <ContentWrapper>
          {renderHeader(
            `Chọn Tài Khoản ${template.name}`,
            `cho ${childUsername}`
          )}
          <ServiceAccountSelector
            childId={childId}
            childUsername={childUsername}
            activityType={config.getServiceName()}
            activityName={template.name}
            csrfToken={csrfToken}
            onAccountReady={config.handleServiceAccountReady}
            onBack={() => config.handleBack(onBack)}
            isTestMode={isTestMode}
            isValidating={config.isValidating}
          />
        </ContentWrapper>
      )}

      {/* Credentials Form */}
      {config.currentStep === 'configure' && (
        <div>
          <ContentWrapper>
            {renderHeader(
              `Cấu Hình ${template.name}`,
              `Nhập thông tin của tài khoản ${template.name}`
            )}
            {config.usesOtpAuth ? (
              <ArtWorkoutOtpForm
                childId={childId}
                csrfToken={csrfToken}
                onSuccess={config.handleArtWorkoutOtpSuccess}
                isTestMode={isTestMode}
              />
            ) : (
              <CredentialsForm
                activityType={template.key}
                activityName={template.name}
                configFields={template.config_fields || []}
                onValidate={config.handleValidateCredentials}
                isValidating={config.isValidating}
                error={config.validationError}
                success={config.validationSuccess}
                isTestMode={isTestMode}
                initialValues={config.credentials}
                submitButtonText={template?.auto_course ? 'Xác nhận' : 'Chọn Khóa Học'}
              />
            )}
          </ContentWrapper>

          <div className="mt-3 d-flex justify-content-between align-items-center">
            <button
              type="button"
              className="btn btn-link text-secondary"
              onClick={() => config.handleBack(onBack)}
              disabled={config.isValidating || config.isSubmitting}
            >
              <i className="bi bi-arrow-left me-2"></i>
              {config.usesOtpAuth ? 'Quay Lại Chọn Hoạt Động' : 'Quay Lại Chọn Tài Khoản'}
            </button>
            {/* Show skip button only when validation fails (wizard mode) */}
            {isWizardMode && config.validationError && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={config.handleSkip}
                disabled={config.isValidating}
                data-testid="skip-activity-btn"
              >
                Bỏ Qua Hoạt Động Này
              </button>
            )}
          </div>
        </div>
      )}

      {/* Preloaded Account Error */}
      {config.currentStep === 'preloaded_error' && (
        <div>
          <ContentWrapper>
            {renderHeader(
              'Lỗi Xác Thực Tài Khoản',
              `Không thể xác thực tài khoản ${template.name}`
            )}
            <div
              className="alert alert-danger"
              role="alert"
              {...(isTestMode && { 'data-testid': 'preloaded-account-error' })}
            >
              <div className="d-flex">
                <div className="flex-shrink-0 me-2">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                </div>
                <div>
                  <strong>Tài khoản Powerkid cung cấp không thể xác thực</strong>
                  <p className="mb-0 mt-1 small">
                    Vui lòng chọn sử dụng tài khoản riêng của bạn.
                  </p>
                </div>
              </div>
            </div>
          </ContentWrapper>

          <div className="mt-3 d-flex justify-content-between align-items-center">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => config.handleBack(onBack)}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Quay Lại Chọn Tài Khoản
            </button>
            {isWizardMode && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={config.handleSkip}
                data-testid="skip-activity-btn"
              >
                Bỏ Qua Hoạt Động Này
              </button>
            )}
          </div>
        </div>
      )}

      {/* Subscription Selection */}
      {config.currentStep === 'subscription' && (
        <div>
          <ContentWrapper>
            {renderHeader(
              'Gói Đăng Ký',
              'Xác nhận loại gói đăng ký của tài khoản'
            )}
            <SubscriptionSelector
              activityType={config.getServiceName()}
              hasSubscription={config.hasSubscription}
              onToggle={config.setHasSubscription}
              isTestMode={isTestMode}
            />
            <div className="d-flex justify-content-end gap-3 pt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => config.handleBack(onBack)}
                disabled={config.isSubmitting}
              >
                {getBackButtonText()}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={config.handleSubscriptionContinue}
                disabled={config.isSubmitting}
                data-testid="subscription-continue-btn"
              >
                {config.isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Đang xử lý...
                  </>
                ) : template?.auto_course ? (
                  getSubmitButtonText()
                ) : (
                  'Chọn Khóa Học'
                )}
              </button>
            </div>
          </ContentWrapper>

          {/* Back to Activity Selection (non-wizard only) */}
          {!isWizardMode && (
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-link text-secondary"
                onClick={onBack}
                disabled={config.isSubmitting}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Quay Lại Chọn Hoạt Động
              </button>
            </div>
          )}
        </div>
      )}

      {/* Course Selection */}
      {config.currentStep === 'courses' && (
        <div>
          <ContentWrapper>
            {renderHeader(
              'Chọn Khóa Học',
              `Chọn khóa học cho ${childUsername}`
            )}
            {template.key === 'codecombat' ? (
              <CourseGroupSelector
                courseGroups={config.courseGroups}
                selectedGroupAlias={config.selectedGroupAlias}
                onGroupSelect={config.handleGroupSelect}
                isLoading={false}
                error={config.submitError}
                isTestMode={isTestMode}
              />
            ) : (
              <CourseSelector
                activityType={template.key}
                activityName={template.name}
                courses={config.coursesData}
                courseStructure={config.courseStructure}
                maxCourses={config.getMaxCourses()}
                selectedCourses={config.selectedCourses}
                onCourseToggle={config.handleCourseToggle}
                isLoading={false}
                error={config.submitError}
                isTestMode={isTestMode}
              />
            )}
            <div className="d-flex justify-content-end gap-3 pt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => config.handleBack(onBack)}
                disabled={config.isSubmitting}
              >
                {getBackButtonText()}
              </button>
              <button
                type="button"
                id="add-activity-submit-btn"
                className="btn btn-primary"
                onClick={config.handleSubmitCourses}
                disabled={config.isSubmitting || config.selectedCourses.length === 0}
                data-testid="submit-activity-btn"
              >
                {config.isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Đang Tạo Hoạt Động...
                  </>
                ) : (
                  getSubmitButtonText()
                )}
              </button>
            </div>
          </ContentWrapper>

          {/* Back to Activity Selection (non-wizard only) */}
          {!isWizardMode && (
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-link text-secondary"
                onClick={onBack}
                disabled={config.isSubmitting}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Quay Lại Chọn Hoạt Động
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityConfigurationFlow;
